import type { Filter, ItemCollectionResponse } from '../types/api';
import type { AuthorizationEntityType } from '../types/flow';
import type {
    DescribeServiceTypesAndActions,
    ObjectDataResponse2API,
    OpenApiAPI,
    ServiceElementRequestAPI,
    ServiceElementResponseAPI,
    ServiceValueRequestAPI,
} from '../types/service';
import type { ObjectAPI } from '../types/value';
import { fetchAndParse, filterToQuery } from '../utils/ajax';
import { getTenantId } from '../utils/tenant';

export const getService = (serviceId: string) =>
    fetchAndParse<ServiceElementResponseAPI>({
        url: `/api/draw/1/element/service/${serviceId}`,
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

export const saveService = (serviceData: ServiceElementRequestAPI) =>
    fetchAndParse<ServiceElementResponseAPI>({
        url: '/api/draw/1/element/service',
        headers: {
            'Content-Type': 'application/json; charset=utf-8',
            ManyWhoTenant: getTenantId(),
        },
        method: 'POST',
        body: JSON.stringify(serviceData),
    });

export const deleteService = (serviceId: string) =>
    // ServiceElementResponseAPI
    fetchAndParse<void>({
        url: `/api/draw/1/element/service/${serviceId}`,
        headers: {
            'Content-Type': 'application/json; charset=utf-8',
            ManyWhoTenant: getTenantId(),
        },
        method: 'DELETE',
    });

export const getAllServices = () =>
    fetchAndParse<ServiceElementResponseAPI[]>({
        url: '/api/draw/1/element/service',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

export const getAllServicesV2 = (filters: Filter) => {
    const query = filterToQuery(filters);
    return fetchAndParse<ItemCollectionResponse<ServiceElementResponseAPI>>({
        url: `/api/draw/2/element/service${query}`,
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });
};

/**
 * If a search term is provided, then the service will attempt to return results based on that.
 * If objectData is provided then the service will attempt to filter results based on that.
 * So for example, to return specific users the objectData might look like:

 [
 {
            developerName: "GroupAuthorizationUser",
            properties: [
                {
                    developerName: "AuthenticationId",
                    contentType: "ContentString",
                    contentValue: "1"
                }
            ]
        },
        {
            developerName: "GroupAuthorizationUser",
            properties: [
                {
                    developerName: "AuthenticationId",
                    contentType: "ContentString",
                    contentValue: "2"
                }
            ]
        }
 ]
 */
export const getServiceGroupsOrUsers = ({
    serviceId,
    type,
    search = null,
    objectData = null,
}: {
    serviceId: string;
    type: AuthorizationEntityType;
    search: string | null;
    objectData: ServiceElementRequestAPI | null;
}) => {
    const body = {
        objectData,
        listFilter: {
            comparisonType: 'VALUE',
            limit: 1000,
            filterByProvidedObjects: objectData !== null, // Used by some services to tell it to filter by objectdata
            search,
        },
    };
    return fetchAndParse<ObjectDataResponse2API>({
        url: `/api/draw/1/element/service/${serviceId}/${type}`,
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json; charset=utf-8',
        },
        body,
    });
};

export const getServiceAuthenticationAttributes = ({
    serviceId,
    type,
}: {
    serviceId: string;
    type: string;
}) =>
    fetchAndParse<ObjectAPI[]>({
        url: `/api/draw/1/element/service/${serviceId}/${type}/authenticationattributes`,
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
        },
    });

export const getServiceConfigurationValues = ({
    uri,
    basicUsername = null,
    basicPassword = null,
    httpAuthenticationClientCertificateReference = null,
    httpAuthenticationClientCertificatePasswordReference = null,
    configurationValues = null,
    id = null,
}: {
    uri: string | undefined;
    basicUsername: string | null | undefined;
    basicPassword: string | null | undefined;
    httpAuthenticationClientCertificateReference: string | null | undefined;
    httpAuthenticationClientCertificatePasswordReference: string | null | undefined;
    configurationValues?: ServiceValueRequestAPI[] | null;
    id: string | null;
}) => {
    const body = {
        uri,
        httpAuthenticationUsername: basicUsername,
        httpAuthenticationPassword: basicPassword,
        HttpAuthenticationClientCertificate: httpAuthenticationClientCertificateReference,
        HttpAuthenticationClientCertificatePassword:
            httpAuthenticationClientCertificatePasswordReference,
        configurationValues,
        id,
    };
    return fetchAndParse<ServiceValueRequestAPI[]>({
        url: '/api/draw/1/element/service/configurationValues',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json; charset=utf-8',
        },
        body,
    });
};

export const getServiceTypesAndActions = ({
    uri,
    basicUsername = null,
    basicPassword = null,
    httpAuthenticationClientCertificateReference = null,
    httpAuthenticationClientCertificatePasswordReference = null,
    configurationValues = null,
    id = null,
    identityProviderId = null,
    openApiId = null,
}: {
    uri: string | undefined;
    basicUsername: string | null | undefined;
    basicPassword: string | null | undefined;
    httpAuthenticationClientCertificateReference: string | null | undefined;
    httpAuthenticationClientCertificatePasswordReference: string | null | undefined;
    configurationValues: ServiceValueRequestAPI[] | null;
    id: string | null | undefined;
    identityProviderId: string | null | undefined;
    openApiId?: string | null | undefined;
}) => {
    const body = {
        uri,
        httpAuthenticationUsername: basicUsername,
        httpAuthenticationPassword: basicPassword,
        HttpAuthenticationClientCertificate: httpAuthenticationClientCertificateReference,
        HttpAuthenticationClientCertificatePassword:
            httpAuthenticationClientCertificatePasswordReference,
        configurationValues,
        id,
        identityProviderId,
        openApiId,
    };
    return fetchAndParse<DescribeServiceTypesAndActions>({
        url: '/api/draw/1/element/service/typesAndActions',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json; charset=utf-8',
        },
        body,
    });
};

export const installService = ({
    developerName,
    developerSummary,
    uri,
    basicUsername = null,
    basicPassword = null,
    httpAuthenticationClientCertificateReference = null,
    httpAuthenticationClientCertificatePasswordReference = null,
    configurationValues = null,
    id = null,
    identityProviderId = null,
    openApiId = null,
}: {
    developerName: string | null | undefined;
    developerSummary: string | null | undefined;
    uri: string | undefined;
    basicUsername: string | null | undefined;
    basicPassword: string | null | undefined;
    httpAuthenticationClientCertificateReference: string | null | undefined;
    httpAuthenticationClientCertificatePasswordReference: string | null | undefined;
    configurationValues: ServiceValueRequestAPI[] | null;
    id: string | null | undefined;
    identityProviderId: string | null | undefined;
    openApiId?: string | null | undefined;
}) => {
    const body = {
        uri,
        developerName,
        developerSummary,
        httpAuthenticationUsername: basicUsername,
        httpAuthenticationPassword: basicPassword,
        HttpAuthenticationClientCertificate: httpAuthenticationClientCertificateReference,
        HttpAuthenticationClientCertificatePassword:
            httpAuthenticationClientCertificatePasswordReference,
        configurationValues,
        id,
        identityProviderId,
        openApiId,
    };
    return fetchAndParse<ServiceElementResponseAPI>({
        url: '/api/draw/1/element/service/install',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json; charset=utf-8',
        },
        body,
    });
};

export const getOpenApiSchemaInfo = ({
    schemaValueId,
    serviceId,
    openApiId,
}: {
    schemaValueId: string | null | undefined;
    serviceId?: string | null | undefined;
    openApiId?: string | null | undefined;
}) => {
    if (!schemaValueId) {
        return;
    }
    const query = openApiId
        ? `?openApiId=${openApiId}`
        : serviceId
          ? `?serviceId=${serviceId}`
          : '';
    return fetchAndParse<OpenApiAPI>({
        url: `/api/draw/1/openapi/info/${schemaValueId}${query}`,
        method: 'GET',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json; charset=utf-8',
        },
    });
};

export const SaveOpenApiSchemaConfig = (openApiAPI: OpenApiAPI) => {
    return fetchAndParse<OpenApiAPI>({
        url: '/api/draw/1/element/openapi',
        method: 'POST',
        headers: {
            ManyWhoTenant: getTenantId(),
            'Content-Type': 'application/json; charset=utf-8',
        },
        body: openApiAPI,
    });
};
